import { Box } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import OverlaySpinner from "../../common/components/functional/overlaySpinner/OverlaySpinner";
import PageTitle from "../../common/components/functional/page/PageTitle";
import { FEEDBACKS_PAGE_SIZE } from "../../common/components/functional/pagination/constants";
import { TablePaginationProps } from "../../common/components/functional/pagination/types";
import { StripedTable } from "../../common/components/functional/stripedTable/StripedTable";
import { useTablePagination } from "../../hooks/useTablePagination";
import FeedbackManagementModal from "./FeedbackManagementModal";
import FeedbackRow from "./FeedbackRow";
import {
  fetchFeedbacks,
  selectAllFeedbacks,
  selectFeedbackById,
} from "./FeedbacksSlice";
import { Feedback } from "./types/queries";

const pageSize = parseInt(FEEDBACKS_PAGE_SIZE, 10);

const FeedbacksPage = () => {
  const [selectedFeedbackId, setSelectedFeedbackId] = useState("");

  const { t } = useTranslation("feedbacks");
  const dispatch = useAppDispatch();

  const feedbacks = useAppSelector(selectAllFeedbacks);
  const { cursor, hits, status } = useAppSelector((state) => state.feedbacks);
  const selectedFeedback = useAppSelector((state) =>
    selectFeedbackById(state, selectedFeedbackId),
  );
  const isLoading = status === "pending";

  const dispatchFetchFeedbacks = useCallback(
    (cursor?: string) => {
      const payload = {
        ...(cursor && { cursor }),
        pageSize,
      };
      dispatch(fetchFeedbacks(payload));
    },
    [dispatch],
  );

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination<Feedback>({
      data: feedbacks,
      cursor: cursor || null,
      hits,
      pageSize,
      fetchNext: (cursor) => dispatchFetchFeedbacks(cursor),
    });

  useEffect(() => {
    dispatchFetchFeedbacks();
    resetPagination();
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const paginationProps: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize,
  };

  const columnHeaders = [
    t("reporter"),
    t("workLocationName"),
    t("type"),
    t("worktimeEntry"),
    t("talent"),
    t("rating"),
    t("comment"),
    t("createdAt"),
    t("acknowledgedBy"),
  ];

  const onClose = () => setSelectedFeedbackId("");
  const onRowClick = (id: string) => setSelectedFeedbackId(id);

  const feedbackRows = pageData.map((feedback) => (
    <FeedbackRow
      key={feedback.targetAggregateId}
      feedback={feedback}
      onClick={onRowClick}
    />
  ));

  return (
    <Box data-testid="feedbacks-page-root">
      <OverlaySpinner isLoading={isLoading} />
      <PageTitle title={t("feedbacks")} />
      <StripedTable
        columnHeaders={columnHeaders}
        itemRows={feedbackRows}
        pagination={paginationProps}
      />
      {selectedFeedback && (
        <FeedbackManagementModal
          open={!!selectedFeedback}
          onClose={onClose}
          feedback={selectedFeedback}
        />
      )}
    </Box>
  );
};

export default FeedbacksPage;
